import React, { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { useAppContext } from '../context/AppContext'
import { useLanguage } from '../context/LanguageContext'
import { translations } from '../translations/translations'
import { FaFileExcel, FaSpinner, FaCheck, FaExclamationTriangle, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa'
import * as XLSX from 'xlsx'
import groupManager from '../utils/groupManager'
import '../styles/EditableGuestTable.css'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000'

// Mirror of backend normalizePhone (handler.js): strips separators and converts
// local Israeli formats to international (972…). Must stay in sync so frontend
// "already-sent" comparisons match the normalized phones stored in messages_status.
const normalizePhone = (raw) => {
  if (raw == null || typeof raw !== 'string') return null
  let phone = raw.replace(/[\s+()\-]/g, '')
  if (!phone) return null
  if (!/^\d+$/.test(phone)) return null
  if (phone.startsWith('00')) phone = phone.slice(2)
  if (phone.startsWith('972')) return phone
  if (phone.startsWith('0')) return '972' + phone.slice(1)
  if (phone.startsWith('5') && phone.length === 9) return '972' + phone
  return null
}

// Helper function to normalize "don't know" values (handles different apostrophes)
const normalizeDontKnow = (value) => {
  if (value === "don't know" || value === "don’t know") {
    return "don't know"
  }
  return value
}

// Helper function to check if a value is "don't know" (handles different apostrophes)
const isDontKnow = (value) => {
  const result = value === "don't know" || value === "don’t know"
  console.log('isDontKnow check:', {
    value,
    value_repr: JSON.stringify(value),
    straight_apostrophe: value === "don't know",
    curly_apostrophe: value === "don’t know",
    result
  })
  return result
}

// Derive a delivery state from the latest messages_status row joined onto each guest.
// Why: statusid alone is unreliable — the webhook updates `status`/`error_code`
// on async failures but never touches statusid, so a green dot from statusid=1
// can hide a later Meta `failed` callback.
const getDeliveryState = (guest) => {
  const status = guest.message_status
  const errorCode = guest.message_error_code
  // Match any error/failure status case-insensitively. Covers consumer's
  // 'FAILED'/'ERROR', webhook's lowercase 'failed', and legacy
  // 'error'/'unhandled_error' that /superadmin/errors filters on.
  const lower = typeof status === 'string' ? status.toLowerCase() : ''
  if (errorCode || lower === 'failed' || lower === 'error' || lower === 'unhandled_error') {
    return 'failed'
  }
  if (lower === 'delivered' || lower === 'read' || lower === 'sent' || lower === 'sent_to_meta') {
    return 'sent'
  }
  if (lower === 'processing') return 'processing'
  if (guest.statusid === 1) return 'sent'
  return 'none'
}

const EditableGuestTable = ({ guests, onGuestsUpdate, sendMessages }) => {
  const { user } = useAppContext()
  const { language } = useLanguage()
  const [editableGuests, setEditableGuests] = useState([])
  const [showExportPopup, setShowExportPopup] = useState(false)
  const [exportFilters, setExportFilters] = useState({
    coming: true,
    notComing: true,
    dontKnow: true
  })
  const [isSavingNewGuest, setIsSavingNewGuest] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [deletedGuestIds, setDeletedGuestIds] = useState([])
  const [validationErrors, setValidationErrors] = useState({})
  const [focusedGuestId, setFocusedGuestId] = useState(null)
  const [editingCell, setEditingCell] = useState(null)
  const [showAddGuestForm, setShowAddGuestForm] = useState(false)
  const [newGuestData, setNewGuestData] = useState({
    firstname: '',
    lastname: '',
    phone_number: '',
    guest_group: '',
    side: '',
    is_coming: "don’t know",
    number_of_guests: 0,
  })
  const [deletingGuestId, setDeletingGuestId] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null) // { id, name } | null
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savingCell, setSavingCell] = useState(null)
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterGroup, setFilterGroup] = useState('')
  const [filterSide, setFilterSide] = useState('')
  const [filterComing, setFilterComing] = useState('')
  const [filterDelivery, setFilterDelivery] = useState('')
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [messageChannel, setMessageChannel] = useState('sms')
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [customizedWaSentPhones, setCustomizedWaSentPhones] = useState(new Set())
  const [showCustomizedConfirm, setShowCustomizedConfirm] = useState(false)
  const [isSendingCustomized, setIsSendingCustomized] = useState(false)
  const [secondWaSentPhones, setSecondWaSentPhones] = useState(new Set())
  const [showSecondWaConfirm, setShowSecondWaConfirm] = useState(false)
  const [isSendingSecondWa, setIsSendingSecondWa] = useState(false)
  const [includeAlreadySentSecond, setIncludeAlreadySentSecond] = useState(false)
  const [includeAlreadySentCustomized, setIncludeAlreadySentCustomized] = useState(false)
  const [originalEditValue, setOriginalEditValue] = useState(null)
  const tableRef = useRef(null)
  const exportButtonRef = useRef(null)
  const exportPopupRef = useRef(null)
  const debounceTimers = useRef({})
  const editableGuestsRef = useRef([])
  const autoSaveGuestRef = useRef(null)
  const editingCellRef = useRef(null)

  // Get pastel color for group (using group manager)
  const getGroupColor = (group) => {
    return groupManager.getGroupColor(group)
  }

  // State for available groups
  const [availableGroups, setAvailableGroups] = useState([])

  // Update available groups when guests change or when groups are manually added
  useEffect(() => {
    const updateAvailableGroups = () => {
      groupManager.updateFromGuestList(guests)
      setAvailableGroups(groupManager.getAllGroups())
    }
    
    // Initial update
    updateAvailableGroups()
    
    // Listen for custom groups updated event
    const handleGroupsUpdated = () => {
      updateAvailableGroups()
    }
    
    window.addEventListener('groupsUpdated', handleGroupsUpdated)
    
    return () => {
      window.removeEventListener('groupsUpdated', handleGroupsUpdated)
    }
  }, [guests])

  // Render group options for select dropdowns
  const renderGroupOptions = () => {
    return (
      <>
        <option value="">בחר קבוצה</option>
        {availableGroups.map(group => (
          <option key={group} value={group}>{group}</option>
        ))}
      </>
    )
  }

  useEffect(() => {
    const editingGuestId = editingCellRef.current?.guestId
    setEditableGuests((prev) =>
      guests.map((guest) => {
        // Preserve the actively-edited row so user typing isn't wiped
        // when a parent refresh fires mid-edit.
        if (editingGuestId && guest.id === editingGuestId) {
          const local = prev.find((g) => g.id === guest.id)
          if (local) return local
        }
        return { ...guest, isNew: false, isDeleted: false }
      })
    )
    if (!editingCellRef.current) {
      setHasChanges(false)
      setValidationErrors({})
      setSaveSuccess(false)
    }
    setDeletedGuestIds([])
  }, [guests])

  useEffect(() => {
    editableGuestsRef.current = editableGuests
  }, [editableGuests])

  useEffect(() => {
    editingCellRef.current = editingCell
  }, [editingCell])

  // Clear all pending debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout)
    }
  }, [])

  // Auto-save success message
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [saveSuccess])

  // Click outside handler for export popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportPopup && 
          exportPopupRef.current && 
          !exportPopupRef.current.contains(event.target) &&
          exportButtonRef.current &&
          !exportButtonRef.current.contains(event.target)) {
        setShowExportPopup(false)
      }
    }

    if (showExportPopup) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportPopup])

  const validateGuest = useCallback((guest) => {
    const errors = {}
    
    if (!guest.firstname?.trim()) {
      errors.firstname = 'שם פרטי נדרש'
    }
    
    // if (!guest.lastname?.trim()) {
    //   errors.lastname = 'שם משפחה נדרש'
    // }
    const cleanPhone = (guest.phone_number || '').replace(/[^\d]/g, '');

    if (!cleanPhone) {
      errors.phone_number = 'מספר טלפון לא תקין';
    }
        
    if (guest.number_of_guests < 0 || guest.number_of_guests > 20) {
      errors.number_of_guests = 'מספר אורחים חייב להיות בין 0 ל-20'
    }
    
    return errors
  }, [])

  const updateGuest = useCallback((id, field, value) => {
    setEditableGuests((prev) => {
      const updated = prev.map((guest) => {
        if (guest.id === id) {
          // Normalize the value for is_coming field
          const normalizedValue = field === 'is_coming' ? normalizeDontKnow(value) : value
          const updatedGuest = { ...guest, [field]: normalizedValue }
          
          // Auto-set number_of_guests to 1 when changing RSVP from "no" or "don't know" to "yes"
          if (field === 'is_coming' && normalizedValue === 'yes' && 
              (guest.is_coming === 'no' || isDontKnow(guest.is_coming))) {
            updatedGuest.number_of_guests = Math.max(1, guest.number_of_guests || 0)
          }
          
          // Auto-set number_of_guests to 0 when changing RSVP from "yes" to something else
          if (field === 'is_coming' && normalizedValue !== 'yes' && guest.is_coming === 'yes') {
            updatedGuest.number_of_guests = 0
          }
          
          // Auto-set RSVP to "no" when changing number_of_guests from >0 to 0
          if (field === 'number_of_guests' && parseInt(normalizedValue) === 0 && 
              parseInt(guest.number_of_guests) > 0 && guest.is_coming === 'yes') {
            updatedGuest.is_coming = 'no'
          }
          
          return updatedGuest
        }
        return guest
      })
      
      // Clear validation errors for this field
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        if (newErrors[id]) {
          delete newErrors[id][field]
          if (Object.keys(newErrors[id]).length === 0) {
            delete newErrors[id]
          }
        }
        return newErrors
      })
      
      return updated
    })
    setHasChanges(true)
  }, [])


  const showAddGuestFormHandler = useCallback(() => {
    setShowAddGuestForm(true)
    setNewGuestData({
      firstname: '',
      lastname: '',
      phone_number: '',
      guest_group: '',
      side: '',
      is_coming: "don't know",
      number_of_guests: 0,
    })
  }, [])

  const hideAddGuestForm = useCallback(() => {
    setShowAddGuestForm(false)
    setNewGuestData({
      firstname: '',
      lastname: '',
      phone_number: '',
      guest_group: '',
      side: '',
      is_coming: "don't know",
      number_of_guests: 0,
    })
  }, [])

  const handleNewGuestFieldChange = useCallback((field, value) => {
    setNewGuestData(prev => ({
      ...prev,
      [field]: value
    }))
  }, [])

  const saveNewGuest = useCallback(async () => {
    // Validate the new guest data
    const errors = validateGuest(newGuestData)
    if (Object.keys(errors).length > 0) {
      alert('יש שגיאות בטופס. אנא תקן אותן לפני השמירה.')
      return
    }

    setIsSavingNewGuest(true)
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/add-guest`,
        {
          firstname: newGuestData.firstname,
          lastname: newGuestData.lastname,
          phone_number: newGuestData.phone_number,
          is_coming: normalizeDontKnow(newGuestData.is_coming),
          number_of_guests: parseInt(newGuestData.number_of_guests) || 0,
          guest_group: newGuestData.guest_group,
          side: newGuestData.side,
        },
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      )

     // Add the new guest to local state immediately for better UX
     const newGuest = {
          id: response.data.id,
          firstname: newGuestData.firstname,
          lastname: newGuestData.lastname,
          phone_number: newGuestData.phone_number,
          is_coming: normalizeDontKnow(newGuestData.is_coming),
          number_of_guests: parseInt(newGuestData.number_of_guests) || 0,
          guest_group: newGuestData.guest_group,
          side: newGuestData.side,
          isNew: false,
          isDeleted: false,
      }
      
      setEditableGuests(prev => [...prev, newGuest])

      alert('האורח נוסף בהצלחה!')
      hideAddGuestForm()
      
      // Also refresh the guest list to ensure consistency with server
      if (onGuestsUpdate) {
        onGuestsUpdate()
      }
    } catch (error) {
      console.error('Error adding guest:', error)
      if (error.response?.status === 409) {
        const existing = error.response.data?.existing
        const existingName = `${existing?.firstname || ''} ${existing?.lastname || ''}`.trim() || 'אורח קיים'
        alert(`מספר הטלפון כבר משויך ל-${existingName}. ערוך את האורח הקיים במקום להוסיף חדש.`)
      } else {
        const errorMessage = error.response?.data?.message || 'שגיאה בהוספת האורח. אנא נסה שוב.'
        alert(errorMessage)
      }
    }
    setIsSavingNewGuest(false)
  }, [newGuestData, validateGuest, user.token, onGuestsUpdate, hideAddGuestForm])

  const deleteGuest = useCallback(async (id, silent = false) => {
    const guest = editableGuests.find((g) => g.id === id)
    if (!guest) return

    setDeletingGuestId(id)
    
    try {
      if (guest.isNew) {
        // Remove new guests immediately (they don't exist on the server)
        setEditableGuests((prev) => prev.filter((g) => g.id !== id))
      } else {
        // Delete existing guest from the server
        await axios.delete(
          `${BASE_URL}/admin/delete-guest/${guest.id}`,
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        )
        
        // Remove from local state after successful deletion
        setEditableGuests((prev) => prev.filter((g) => g.id !== id))
        
        // Refresh the guest list to ensure consistency
        if (onGuestsUpdate) {
          onGuestsUpdate()
        }
      }
      
      // Clear validation errors for this guest
      setValidationErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        delete newErrors[id]
        return newErrors
      })
      
      // Clear editing state if this guest was being edited
      if (editingCell?.guestId === id) {
        setEditingCell(null)
      }
      
    } catch (error) {
      console.error('Error deleting guest:', error)
      if (!silent) {
        const errorMessage = error.response?.data?.message || 'שגיאה במחיקת האורח. אנא נסה שוב.'
        alert(errorMessage)
      }
      throw error // Re-throw for bulk operations
    } finally {
      setDeletingGuestId(null)
    }
  }, [editableGuests, user.token, onGuestsUpdate, editingCell])

  const requestDeleteGuest = useCallback((id) => {
    const guest = editableGuests.find((g) => g.id === id)
    const name = `${guest?.firstname || ''} ${guest?.lastname || ''}`.trim() || 'אורח זה'
    setDeleteConfirm({ id, name })
  }, [editableGuests])

  const confirmDeleteGuest = useCallback(async () => {
    if (!deleteConfirm) return
    const { id } = deleteConfirm
    setDeleteConfirm(null)
    try {
      await deleteGuest(id, false)
    } catch (err) {
      // alert already surfaced in deleteGuest
    }
  }, [deleteConfirm, deleteGuest])

 
  const handleCellClick = useCallback((guestId, field) => {
    if (isSaving) return 
    const guest = editableGuests.find(g => g.id === guestId)
    if (guest?.isDeleted) return

    // Store the original value when starting to edit
    setOriginalEditValue(guest[field])
    setEditingCell({ guestId, field })
  }, [isSaving, editableGuests])

  // Auto-save individual guest changes
  // skipRefresh: true for debounced saves (user still editing — don't reset state from server)
  const autoSaveGuest = useCallback(async (guestId, guestDataOverride = null, skipRefresh = false) => {
    const guest = guestDataOverride || editableGuestsRef.current.find(g => g.id === guestId)
    if (!guest) return

    // Validate the guest
    const guestErrors = validateGuest(guest)
    if (Object.keys(guestErrors).length > 0) {
      setValidationErrors(prev => ({ ...prev, [guestId]: guestErrors }))
      return
    }

    // Clear validation errors for this guest
    setValidationErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[guestId]
      return newErrors
    })

    setSavingCell({ guestId, field: editingCell?.field })

    try {
      if (guest.isNew && guest.firstname.trim()) {
        // Add new guest
        const response = await axios.post(
          `${BASE_URL}/admin/add-guest`,
          {
            firstname: guest.firstname,
            lastname: guest.lastname,
            phone_number: guest.phone_number,
            is_coming: guest.is_coming,
            number_of_guests: parseInt(guest.number_of_guests) || 0,
            guest_group: guest.guest_group || '',
            side: guest.side || '',
          },
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        )

        // Update the guest with the real ID from the server
        setEditableGuests(prev => 
          prev.map(g => 
            g.id === guestId 
              ? { ...g, id: response.data.id, isNew: false }
              : g
          )
        )
      } else if (!guest.isNew && !guest.isDeleted) {
        // Update existing guest
        console.log('Updating guest:', guest)
        await axios.put(
          `${BASE_URL}/admin/update-guest/${guest.id}`,
          {
            phone_number: guest.phone_number,
            firstname: guest.firstname,
            lastname: guest.lastname,
            is_coming: normalizeDontKnow(guest.is_coming),
            number_of_guests: parseInt(guest.number_of_guests) || 0,
            guest_group: guest.guest_group || '',
            side: guest.side || '',
          },
          {
            headers: { Authorization: `Bearer ${user.token}` },
          }
        )
      }

      setHasChanges(false)
      setSaveSuccess(true)
      
      // Clear save success after 2 seconds
      setTimeout(() => setSaveSuccess(false), 2000)
      
      // Refresh only when not in the middle of an active edit (debounced saves skip this)
      if (onGuestsUpdate && !skipRefresh) {
        onGuestsUpdate()
      }
    } catch (error) {
      console.error('Error auto-saving guest:', error)
      const isDuplicatePhone = error.response?.status === 409
      const message = isDuplicatePhone
        ? 'מספר טלפון כבר משויך לאורח אחר'
        : 'שגיאה בשמירה — לחץ שוב לנסות'
      setValidationErrors(prev => ({
        ...prev,
        [guestId]: {
          ...prev[guestId],
          ...(isDuplicatePhone ? { phone_number: message } : {}),
          general: message,
        }
      }))
    } finally {
      setSavingCell(null)
    }
  }, [validateGuest, user.token, onGuestsUpdate, editingCell?.field])

  // Keep autoSaveGuestRef current so debounce timers always call the latest version
  useEffect(() => {
    autoSaveGuestRef.current = autoSaveGuest
  }, [autoSaveGuest])

  // Debounced save for text inputs — fires 800ms after last keystroke
  const triggerDebouncedSave = useCallback((guestId) => {
    if (debounceTimers.current[guestId]) {
      clearTimeout(debounceTimers.current[guestId])
    }
    debounceTimers.current[guestId] = setTimeout(() => {
      autoSaveGuestRef.current(guestId, null, true) // skipRefresh — user may still be editing
      delete debounceTimers.current[guestId]
    }, 800)
  }, [])

  // Immediate save for dropdown fields — closes edit mode right away
  const handleDropdownChange = useCallback((guestId, field, value) => {
    if (debounceTimers.current[guestId]) {
      clearTimeout(debounceTimers.current[guestId])
      delete debounceTimers.current[guestId]
    }

    const currentGuest = editableGuestsRef.current.find(g => g.id === guestId)
    if (!currentGuest) return

    const normalizedValue = field === 'is_coming' ? normalizeDontKnow(value) : value
    let updatedGuest = { ...currentGuest, [field]: normalizedValue }

    // Mirror the auto-adjustment logic from updateGuest
    if (field === 'is_coming' && normalizedValue === 'yes' &&
        (currentGuest.is_coming === 'no' || isDontKnow(currentGuest.is_coming))) {
      updatedGuest.number_of_guests = Math.max(1, currentGuest.number_of_guests || 0)
    }
    if (field === 'is_coming' && normalizedValue !== 'yes' && currentGuest.is_coming === 'yes') {
      updatedGuest.number_of_guests = 0
    }

    updateGuest(guestId, field, value)
    setEditingCell(null)
    setOriginalEditValue(null)
    autoSaveGuest(guestId, updatedGuest)
  }, [updateGuest, autoSaveGuest])

  // Handle click outside to save current edit
  const handleClickOutside = useCallback(async (event) => {
    if (editingCell && !event.target.closest('.cell-editing')) {
      // Cancel pending debounce and save immediately
      if (debounceTimers.current[editingCell.guestId]) {
        clearTimeout(debounceTimers.current[editingCell.guestId])
        delete debounceTimers.current[editingCell.guestId]
      }
      await autoSaveGuest(editingCell.guestId)
      setEditingCell(null)
      setOriginalEditValue(null)
      
      // Only force re-render if the value actually changed and affects sort
      const currentGuest = editableGuests.find(g => g.id === editingCell.guestId)
      if (currentGuest && 
          editingCell.field === sortColumn && 
          originalEditValue !== null && 
          currentGuest[editingCell.field] !== originalEditValue) {
        setTimeout(() => {
          setEditableGuests(prev => [...prev])
        }, 0)
      }
    }
  }, [editingCell, autoSaveGuest, editableGuests, sortColumn, originalEditValue])

  // Handle enter key to save current edit
  const handleKeyDown = useCallback(async (event) => {
    if (event.key === 'Enter' && editingCell) {
      // Cancel pending debounce and save immediately
      if (debounceTimers.current[editingCell.guestId]) {
        clearTimeout(debounceTimers.current[editingCell.guestId])
        delete debounceTimers.current[editingCell.guestId]
      }
      await autoSaveGuest(editingCell.guestId)
      const currentGuest = editableGuests.find(g => g.id === editingCell.guestId)
      const fieldChanged = currentGuest && 
                          editingCell.field === sortColumn && 
                          originalEditValue !== null && 
                          currentGuest[editingCell.field] !== originalEditValue
      
      setEditingCell(null)
      setOriginalEditValue(null)
      
      // Only force re-render if the value actually changed and affects sort
      if (fieldChanged) {
        setTimeout(() => {
          setEditableGuests(prev => [...prev])
        }, 0)
      }
    } else if (event.key === 'Escape' && editingCell) {
      // Cancel pending debounced save and revert the cell to its original value
      if (debounceTimers.current[editingCell.guestId]) {
        clearTimeout(debounceTimers.current[editingCell.guestId])
        delete debounceTimers.current[editingCell.guestId]
      }
      const { guestId, field } = editingCell
      setEditableGuests((prev) =>
        prev.map((g) =>
          g.id === guestId ? { ...g, [field]: originalEditValue } : g
        )
      )
      setValidationErrors((prev) => {
        const next = { ...prev }
        if (next[guestId]) {
          delete next[guestId][field]
          if (Object.keys(next[guestId]).length === 0) delete next[guestId]
        }
        return next
      })
      setEditingCell(null)
      setOriginalEditValue(null)
    }
  }, [editingCell, autoSaveGuest, editableGuests, sortColumn, originalEditValue])

  // Add event listeners for clicking outside and keyboard events
  useEffect(() => {
    if (editingCell) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [editingCell, handleClickOutside, handleKeyDown])

  const exportToExcel = useCallback(() => {
    // Filter guests based on export filters
    const dataToExport = editableGuests
      .filter((guest) => {
        if (guest.isDeleted) return false
        
        // Check if guest matches any of the selected filters
        const isComingYes = guest.is_coming === 'yes'
        const isComingNo = guest.is_coming === 'no'
        const isComingDontKnow = isDontKnow(guest.is_coming)
        
        // Debug logging
        console.log('Export filter debug for guest:', guest.firstname, {
          is_coming: guest.is_coming,
          is_coming_type: typeof guest.is_coming,
          is_coming_repr: JSON.stringify(guest.is_coming),
          isComingYes,
          isComingNo,
          isComingDontKnow,
          exportFilters
        })
        
        return (
          (exportFilters.coming && isComingYes) ||
          (exportFilters.notComing && isComingNo) ||
          (exportFilters.dontKnow && isComingDontKnow)
        )
      })
      .map((guest) => ({
        'שם פרטי': guest.firstname || '',
        'שם משפחה': guest.lastname || '',
        'טלפון': guest.phone_number || '',
        'קבוצה': guest.guest_group || '',
        'צד': guest.side || '',
        'מגיע/ה': guest.is_coming === 'yes' ? 'מגיע' : guest.is_coming === 'no' ? 'לא מגיע' : 'לא יודע',
        'מספר אורחים': guest.number_of_guests || 0,
      }))

    if (dataToExport.length === 0) {
      alert('אין אורחים לייצוא עם הפילטרים הנבחרים')
      return
    }

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(dataToExport)

    // Set column widths for better readability
    const colWidths = [
      { wch: 15 }, // שם פרטי
      { wch: 15 }, // שם משפחה
      { wch: 15 }, // טלפון
      { wch: 12 }, // קבוצה
      { wch: 10 }, // צד
      { wch: 12 }, // מגיע/ה
      { wch: 12 }, // מספר אורחים
    ]
    worksheet['!cols'] = colWidths

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'רשימת מוזמנים')

    // Generate filename with current date
    const now = new Date()
    const dateStr = now.toISOString().split('T')[0] // YYYY-MM-DD format
    const filename = `רשימת_מוזמנים_${dateStr}.xlsx`

    // Save the file
    XLSX.writeFile(workbook, filename)
    
    // Close the export popup
    setShowExportPopup(false)
  }, [editableGuests, exportFilters])

  const exportToIplanExcel = useCallback(() => {
    const filtered = editableGuests
      .filter((guest) => {
        if (guest.isDeleted) return false
        const isComingYes = guest.is_coming === 'yes'
        const isComingNo = guest.is_coming === 'no'
        const isComingDontKnow = isDontKnow(guest.is_coming)
        return (
          (exportFilters.coming && isComingYes) ||
          (exportFilters.notComing && isComingNo) ||
          (exportFilters.dontKnow && isComingDontKnow)
        )
      })

    if (filtered.length === 0) {
      alert('אין אורחים לייצוא עם הפילטרים הנבחרים')
      return
    }

    // Row 0 — group header labels (merged cells set below)
    const groupRow = ['', '', 'שיוך', '', 'פרטי התקשרות', '', '', 'כתובת', '', '', '', '']
    // Row 1 — column headers
    const colRow = [
      'הזמנה לכבוד',
      "מס' אורחים שהוזמנו",
      'צד',
      'קבוצה',
      'סלולרי',
      'טלפון רגיל',
      'אימייל',
      'עיר',
      'רחוב',
      'מיקוד',
      'תא דואר',
      "צ'ק צפוי",
    ]
    // Data rows
    const dataRows = filtered.map((g) => [
      [g.firstname, g.lastname].filter(Boolean).join(' '),
      g.number_of_guests || '',
      g.side || '',
      g.guest_group || '',
      g.phone_number || '',
      '', // טלפון רגיל
      '', // אימייל
      '', // עיר
      '', // רחוב
      '', // מיקוד
      '', // תא דואר
      '', // צ'ק צפוי
    ])

    const ws = XLSX.utils.aoa_to_sheet([groupRow, colRow, ...dataRows])

    // Merged cells for group headers
    ws['!merges'] = [
      { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } },  // שיוך: C1:D1
      { s: { r: 0, c: 4 }, e: { r: 0, c: 6 } },  // פרטי התקשרות: E1:G1
      { s: { r: 0, c: 7 }, e: { r: 0, c: 11 } }, // כתובת: H1:L1
    ]

    // Column widths
    ws['!cols'] = [
      { wch: 22 }, // הזמנה לכבוד
      { wch: 14 }, // מס' אורחים
      { wch: 10 }, // צד
      { wch: 16 }, // קבוצה
      { wch: 14 }, // סלולרי
      { wch: 12 }, // טלפון רגיל
      { wch: 18 }, // אימייל
      { wch: 12 }, // עיר
      { wch: 16 }, // רחוב
      { wch: 8  }, // מיקוד
      { wch: 8  }, // תא דואר
      { wch: 10 }, // צ'ק צפוי
    ]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'הזמנות')

    const dateStr = new Date().toISOString().split('T')[0]
    XLSX.writeFile(wb, `הזמנות_iPlan_${dateStr}.xlsx`)
    setShowExportPopup(false)
  }, [editableGuests, exportFilters])

  const exportToPatricksExcel = useCallback(() => {
    const dataToExport = editableGuests
      .filter((guest) => !guest.isDeleted && (
        (exportFilters.coming && guest.is_coming === 'yes') ||
        (exportFilters.notComing && guest.is_coming === 'no') ||
        (exportFilters.dontKnow && isDontKnow(guest.is_coming))
      ))
      .map((guest) => ({
        'שם המזמין (מי הזמין את האורח)': guest.side || '',
        'שם הקבוצה': guest.guest_group || '',
        'שמות האורחים בהזמנה': [guest.firstname, guest.lastname].filter(Boolean).join(' '),
        'מספר טלפון': guest.phone_number || '',
        'צפי כמות': guest.number_of_guests || 0,
        'הערות': '',
      }))

    if (dataToExport.length === 0) {
      alert('אין אורחים לייצוא עם הפילטרים הנבחרים')
      return
    }

    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(dataToExport)
    XLSX.utils.book_append_sheet(workbook, worksheet, 'רשימת מוזמנים')

    const dateStr = new Date().toISOString().split('T')[0]
    XLSX.writeFile(workbook, `הזמנות_Patricks_${dateStr}.xlsx`)
    setShowExportPopup(false)
  }, [editableGuests, exportFilters])

  const handleExportButtonClick = () => {
    setShowExportPopup(prev => !prev)
  }

  const handleExportFilterChange = (filterType) => {
    setExportFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }))
  }

  const closeExportPopup = () => {
    setShowExportPopup(false)
  }


  const handleSort = useCallback((column) => {
    let direction = 'asc'
    if (sortColumn === column && sortDirection === 'asc') {
      direction = 'desc'
    }
    setSortColumn(column)
    setSortDirection(direction)
  }, [sortColumn, sortDirection])

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort />
    return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />
  }

  const sortGuests = useCallback((guests) => {
    if (!sortColumn) return guests

    // Don't re-sort if we're currently editing a cell that affects the sort order
    // AND the value has actually changed from the original
    if (editingCell && editingCell.field === sortColumn && originalEditValue !== null) {
      const currentGuest = guests.find(g => g.id === editingCell.guestId)
      if (currentGuest && currentGuest[editingCell.field] !== originalEditValue) {
        return guests
      }
    }

    return [...guests].sort((a, b) => {
      let aValue = a[sortColumn]
      let bValue = b[sortColumn]

      // Handle different data types
      if (sortColumn === 'number_of_guests') {
        aValue = parseInt(aValue) || 0
        bValue = parseInt(bValue) || 0
      } else if (sortColumn === 'is_coming') {
        // Custom sort order for attendance status
        const order = { 'yes': 1, "don't know": 2, 'no': 3 }
        aValue = order[normalizeDontKnow(aValue)] || 4
        bValue = order[normalizeDontKnow(bValue)] || 4
      } else if (sortColumn === 'statusid') {
        // Sort by derived delivery state so order matches the dot color.
        const order = { failed: 1, sent: 2, processing: 3, none: 4 }
        aValue = order[getDeliveryState(a)]
        bValue = order[getDeliveryState(b)]
      } else {
        // String comparison for other columns
        aValue = String(aValue || '').toLowerCase()
        bValue = String(bValue || '').toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [sortColumn, sortDirection, editingCell, originalEditValue])

  const filterGuests = useCallback((guests) => {
    let filtered = guests

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(guest => 
        `${guest.firstname || ''} ${guest.lastname || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (guest.phone_number || '').includes(searchTerm) ||
        (guest.guest_group || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply group filter
    if (filterGroup) {
      filtered = filtered.filter(guest => guest.guest_group === filterGroup)
    }

    // Apply side filter
    if (filterSide) {
      filtered = filtered.filter(guest => guest.side === filterSide)
    }

    // Apply coming status filter
    if (filterComing) {
      filtered = filtered.filter(guest => {
        // Normalize both values to handle different apostrophe types
        const normalizedGuestStatus = normalizeDontKnow(guest.is_coming)
        const normalizedFilterStatus = normalizeDontKnow(filterComing)
        return normalizedGuestStatus === normalizedFilterStatus
      })
    }

    // Apply delivery state filter (red/green/grey dot)
    if (filterDelivery) {
      filtered = filtered.filter(guest => {
        const state = getDeliveryState(guest)
        if (filterDelivery === 'grey') return state === 'none' || state === 'processing'
        return state === filterDelivery
      })
    }

    return filtered
  }, [searchTerm, filterGroup, filterSide, filterComing, filterDelivery])

  // Row selection handlers
  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const currentVisibleGuests = sortGuests(filterGuests(editableGuests.filter((guest) => !guest.isDeleted)))
      const allIds = currentVisibleGuests.map(guest => guest.id)
      setSelectedRows(new Set(allIds))
    } else {
      setSelectedRows(new Set())
    }
  }, [editableGuests, sortGuests, filterGuests])

  const handleSelectRow = useCallback((guestId, checked) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(guestId)
      } else {
        newSet.delete(guestId)
      }
      return newSet
    })
  }, [])

  const handleBulkDelete = useCallback(() => {
    if (selectedRows.size === 0) return
    setBulkDeleteConfirm(true)
  }, [selectedRows])

  const confirmBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) return
    setBulkDeleteConfirm(false)
    setIsSaving(true)
    let succeeded = 0
    const failedIds = []
    for (const guestId of selectedRows) {
      try {
        await deleteGuest(guestId, true) // silent — modal already confirmed
        succeeded++
      } catch (err) {
        failedIds.push(guestId)
      }
    }
    setSelectedRows(new Set())
    setShowBulkActions(false)
    setIsSaving(false)
    if (failedIds.length === 0) {
      alert(`${succeeded} אורחים נמחקו בהצלחה`)
    } else {
      alert(`נמחקו ${succeeded} מתוך ${succeeded + failedIds.length} אורחים. ${failedIds.length} נכשלו.`)
    }
  }, [selectedRows, deleteGuest])

  const handleSendMessage = useCallback(async () => {
    if (selectedRows.size === 0 ) return
    
    setIsSendingMessage(true)
    try {
      const selectedGuests = editableGuests.filter(guest => selectedRows.has(guest.id))
      const guestsWithPhone = selectedGuests.filter(guest => guest.phone_number)
      console.log('Selected guests:', selectedGuests)
      console.log('Guests with phone numbers:', guestsWithPhone)
      
      if (guestsWithPhone.length === 0) {
        alert('אין אורחים עם מספר טלפון באורחים הנבחרים')
        return
      }
      
      // Here you would integrate with your SMS/messaging service
      // For now, we'll just show a success message
      console.log('Sending message to guests:', guestsWithPhone.map(g => g.phone_number))
      const res = await sendMessages("send-message", {phoneNumbers: guestsWithPhone.map(g => g.phone_number)})
      console.log('Message sent response:', res)

      alert(`הודעה נשלחה ל-${guestsWithPhone.length} אורחים`)
      
      // Clear selection and close modal
      setSelectedRows(new Set())
      setShowBulkActions(false)
      setShowMessageModal(false)
    } catch (error) {
      console.error('Error sending message:', error)
      alert('שגיאה בשליחת ההודעה')
    } finally {
      setIsSendingMessage(false)
    }
  }, [selectedRows, editableGuests])

  const handleSendWhatsAppFirstMessage = useCallback(async () => {
    if (selectedRows.size === 0) return

    setIsSendingMessage(true)
    try {
      const selectedGuests = editableGuests.filter(guest => selectedRows.has(guest.id))
      const guestsWithPhone = selectedGuests.filter(guest => guest.phone_number)

      if (guestsWithPhone.length === 0) {
        alert('אין אורחים עם מספר טלפון באורחים הנבחרים')
        return
      }

      const res = await sendMessages("send-first-wa-message", {
        phoneNumbers: guestsWithPhone.map(g => g.phone_number),
      })
      console.log('WhatsApp first message response:', res)

      alert(`הודעת וואטסאפ נשלחה ל-${guestsWithPhone.length} אורחים`)

      setSelectedRows(new Set())
      setShowBulkActions(false)
      setShowMessageModal(false)
    } catch (error) {
      console.error('Error sending WhatsApp message:', error)
      alert('שגיאה בשליחת הודעת וואטסאפ')
    } finally {
      setIsSendingMessage(false)
    }
  }, [selectedRows, editableGuests, sendMessages])

  // Load phones already sent the customized_model template (admin only)
  useEffect(() => {
    if (!user?.isAdmin) return
    axios.get(`${BASE_URL}/admin/customized-whatsapp-sent-phones`,
      { headers: { Authorization: `Bearer ${user.token}` } })
      .then(res => setCustomizedWaSentPhones(new Set(res.data.sentPhones)))
      .catch(err => console.error('[customized-sent-phones] Failed to load', err))
  }, [user?.isAdmin, user?.token])

  // Load phones already sent rsvp_invitation_second_ut (admin only)
  useEffect(() => {
    if (!user?.isAdmin) return
    axios.get(`${BASE_URL}/admin/second-wa-sent-phones`,
      { headers: { Authorization: `Bearer ${user.token}` } })
      .then(res => setSecondWaSentPhones(new Set(res.data.sentPhones)))
      .catch(err => console.error('[second-wa-sent-phones] Failed to load', err))
  }, [user?.isAdmin, user?.token])

  const handleSelectFirst10Eligible = useCallback(() => {
    const eligible = sortGuests(filterGuests(
      editableGuests.filter(g => !g.isDeleted && g.phone_number && !customizedWaSentPhones.has(normalizePhone(g.phone_number)))
    ))
    setSelectedRows(new Set(eligible.slice(0, 10).map(g => g.id)))
  }, [editableGuests, customizedWaSentPhones, sortGuests, filterGuests])

  const handleSelectFirst1000Eligible = useCallback(() => {
    const eligible = sortGuests(filterGuests(
      editableGuests.filter(g => !g.isDeleted && g.phone_number && !customizedWaSentPhones.has(normalizePhone(g.phone_number)))
    ))
    setSelectedRows(new Set(eligible.slice(0, 1000).map(g => g.id)))
  }, [editableGuests, customizedWaSentPhones, sortGuests, filterGuests])

  const handleSelectNext10Eligible = useCallback(() => {
    setSelectedRows(prev => {
      const eligible = sortGuests(filterGuests(
        editableGuests.filter(g => !g.isDeleted && g.phone_number && !customizedWaSentPhones.has(normalizePhone(g.phone_number)) && !prev.has(g.id))
      ))
      return new Set([...prev, ...eligible.slice(0, 10).map(g => g.id)])
    })
  }, [editableGuests, customizedWaSentPhones, sortGuests, filterGuests])

  const handleSelectNext1000Eligible = useCallback(() => {
    setSelectedRows(prev => {
      const eligible = sortGuests(filterGuests(
        editableGuests.filter(g => !g.isDeleted && g.phone_number && !customizedWaSentPhones.has(normalizePhone(g.phone_number)) && !prev.has(g.id))
      ))
      return new Set([...prev, ...eligible.slice(0, 1000).map(g => g.id)])
    })
  }, [editableGuests, customizedWaSentPhones, sortGuests, filterGuests])

  const handleSelectFirst10EligibleSecond = useCallback(() => {
    const eligible = sortGuests(filterGuests(
      editableGuests.filter(g => !g.isDeleted && g.phone_number && !secondWaSentPhones.has(normalizePhone(g.phone_number)))
    ))
    setSelectedRows(new Set(eligible.slice(0, 10).map(g => g.id)))
  }, [editableGuests, secondWaSentPhones, sortGuests, filterGuests])

  const handleSelectFirst1000EligibleSecond = useCallback(() => {
    const eligible = sortGuests(filterGuests(
      editableGuests.filter(g => !g.isDeleted && g.phone_number && !secondWaSentPhones.has(normalizePhone(g.phone_number)))
    ))
    setSelectedRows(new Set(eligible.slice(0, 1000).map(g => g.id)))
  }, [editableGuests, secondWaSentPhones, sortGuests, filterGuests])

  const handleSelectNext10EligibleSecond = useCallback(() => {
    setSelectedRows(prev => {
      const eligible = sortGuests(filterGuests(
        editableGuests.filter(g => !g.isDeleted && g.phone_number && !secondWaSentPhones.has(normalizePhone(g.phone_number)) && !prev.has(g.id))
      ))
      return new Set([...prev, ...eligible.slice(0, 10).map(g => g.id)])
    })
  }, [editableGuests, secondWaSentPhones, sortGuests, filterGuests])

  const handleSelectNext1000EligibleSecond = useCallback(() => {
    setSelectedRows(prev => {
      const eligible = sortGuests(filterGuests(
        editableGuests.filter(g => !g.isDeleted && g.phone_number && !secondWaSentPhones.has(normalizePhone(g.phone_number)) && !prev.has(g.id))
      ))
      return new Set([...prev, ...eligible.slice(0, 1000).map(g => g.id)])
    })
  }, [editableGuests, secondWaSentPhones, sortGuests, filterGuests])

  const handleSendSecondWa = useCallback(async () => {
    if (selectedRows.size === 0) return
    setIsSendingSecondWa(true)
    setShowSecondWaConfirm(false)
    try {
      const res = await axios.post(
        `${BASE_URL}/admin/send-second-wa-by-guests`,
        { guestIds: [...selectedRows], forceResend: includeAlreadySentSecond },
        { headers: { Authorization: `Bearer ${user.token}` } }
      )
      const { enqueued, skippedDuplicates, invalidPhones, failed } = res.data
      const sentRes = await axios.get(`${BASE_URL}/admin/second-wa-sent-phones`,
        { headers: { Authorization: `Bearer ${user.token}` } })
      setSecondWaSentPhones(new Set(sentRes.data.sentPhones))
      setSelectedRows(new Set())
      alert(`נשלח: ${enqueued}, דולגו כפילויות: ${skippedDuplicates}, טלפון לא תקין: ${invalidPhones}, שגיאות: ${failed}`)
    } catch (error) {
      console.error('Error sending second WA:', error)
      alert('שגיאה בשליחת הודעת וואטסאפ שנייה')
    } finally {
      setIsSendingSecondWa(false)
    }
  }, [selectedRows, user.token, includeAlreadySentSecond])

  const handleSendCustomizedWhatsApp = useCallback(async () => {
    if (selectedRows.size === 0) return
    setIsSendingCustomized(true)
    setShowCustomizedConfirm(false)
    try {
      const res = await axios.post(
        `${BASE_URL}/admin/send-customized-whatsapp`,
        { guestIds: [...selectedRows], forceResend: includeAlreadySentCustomized },
        { headers: { Authorization: `Bearer ${user.token}` } }
      )
      const { enqueued, skippedDuplicates, invalidPhones, failed } = res.data
      // Refresh sent-phones set
      const sentRes = await axios.get(`${BASE_URL}/admin/customized-whatsapp-sent-phones`,
        { headers: { Authorization: `Bearer ${user.token}` } })
      setCustomizedWaSentPhones(new Set(sentRes.data.sentPhones))
      setSelectedRows(new Set())
      alert(`נשלח: ${enqueued}, דולגו כפילויות: ${skippedDuplicates}, טלפון לא תקין: ${invalidPhones}, שגיאות: ${failed}`)
    } catch (error) {
      console.error('Error sending customized WA:', error)
      alert('שגיאה בשליחת הודעת וואטסאפ מותאמת')
    } finally {
      setIsSendingCustomized(false)
    }
  }, [selectedRows, user.token, includeAlreadySentCustomized])

  // Clear selection when guests change
  useEffect(() => {
    setSelectedRows(new Set())
  }, [guests])

  // Show/hide bulk actions based on selection
  useEffect(() => {
    setShowBulkActions(selectedRows.size > 0)
  }, [selectedRows])

  const getRsvpOptions = () => {
    return [
      { value: "", label: "בחר סטטוס" },
      { value: "yes", label: "מגיע" },
      { value: "no", label: "לא מגיע" },
      { value: "don't know", label: "לא יודע" }
    ]
  }

  const getStatusIndicator = (guest) => {
    const state = getDeliveryState(guest)
    const colors = {
      sent: '#4CAF50',       // green
      failed: '#F44336',     // red
      processing: '#9E9E9E', // grey
      none: '#9E9E9E',       // grey
    }
    const titles = {
      sent: 'הודעה נשלחה בהצלחה',
      failed: 'שליחת ההודעה נכשלה',
      processing: 'ההודעה בתהליך שליחה',
      none: 'לא נשלחה הודעה',
    }
    return (
      <span
        className="status-indicator"
        style={{
          display: 'inline-block',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: colors[state]
        }}
        title={titles[state]}
      />
    )
  }

  // Filter out deleted guests for display and apply sorting and searching
  const visibleGuests = sortGuests(filterGuests(editableGuests.filter((guest) => !guest.isDeleted)))
  
  // Calculate if all visible guests are selected
  const allVisibleSelected = visibleGuests.length > 0 && selectedRows.size === visibleGuests.length

  return (
    <div className={`editable-guest-table ${isSaving ? 'loading' : ''}`}>

      <div className="table-controls">
        <div className="left-controls">
          <button
            ref={exportButtonRef}
            className="export-excel-btn"
            onClick={handleExportButtonClick}
            disabled={isSaving}
          >
            <FaFileExcel style={{ color: '#1a5c36', fill: '#1a5c36', fontSize: 15 }} />
            הורד לאקסל
          </button>
        </div>
      {/* Export Guests Popup */}
      {showExportPopup && (
          <div className="export-popup-content"  ref={exportPopupRef}>
            <h3>ייצוא אורחים לאקסל</h3>
            <p>בחר את סוגי האורחים לייצוא:</p>
            <div className="export-filters">
              <label>
                <input
                  type="checkbox"
                  checked={exportFilters.coming}
                  onChange={() => handleExportFilterChange('coming')}
                />
                <span className="export-filter-checkbox" />
                מגיעים
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={exportFilters.notComing}
                  onChange={() => handleExportFilterChange('notComing')}
                />
                <span className="export-filter-checkbox" />
                לא מגיעים
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={exportFilters.dontKnow}
                  onChange={() => handleExportFilterChange('dontKnow')}
                />
                <span className="export-filter-checkbox" />
                לא יודעים
              </label>
            </div>
            <div className="export-popup-actions">
              <button
                className="export-confirm-btn"
                onClick={exportToExcel}
              >
                ייצוא רגיל
              </button>
              <button
                className="export-confirm-btn export-iplan-btn"
                onClick={exportToIplanExcel}
              >
                ייצוא תבנית iPlan
              </button>
              <button
                className="export-confirm-btn"
                onClick={exportToPatricksExcel}
              >
                ייצוא תבנית Patricks
              </button>
              <button
                className="export-cancel-btn"
                onClick={closeExportPopup}
              >
                ביטול
              </button>
            </div>
          </div>
      )}
        <div className="right-controls">
          <button
            className="add-guest-btn"
            onClick={showAddGuestFormHandler}
            disabled={isSaving}
          >
            + הוסף אורח חדש
          </button>

        </div>
      </div>

      {/* Add Guest Form */}
      {showAddGuestForm && (
        <div className="add-guest-form">
          <div className="form-header">
            <h3>הוסף אורח חדש</h3>
            <button 
              className="close-form-btn"
              onClick={hideAddGuestForm}
              type="button"
            >
              ✕
            </button>
          </div>
          <div className="form-fields">
            <div className="form-field">
              <label htmlFor="new-firstname">שם פרטי *</label>
              <input
                id="new-firstname"
                type="text"
                value={newGuestData.firstname}
                onChange={(e) => handleNewGuestFieldChange('firstname', e.target.value)}
                placeholder="שם פרטי"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="new-lastname">שם משפחה *</label>
              <input
                id="new-lastname"
                type="text"
                value={newGuestData.lastname}
                onChange={(e) => handleNewGuestFieldChange('lastname', e.target.value)}
                placeholder="שם משפחה"
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="new-phone">טלפון</label>
              <input
                id="new-phone"
                type="text"
                value={newGuestData.phone_number}
                onChange={(e) => handleNewGuestFieldChange('phone_number', e.target.value)}
                placeholder="מספר טלפון"
              />
            </div>
            <div className="form-field">
              <label htmlFor="new-group-type">קבוצה</label>
              <select
                id="new-group-type"
                value={newGuestData.guest_group}
                onChange={(e) => handleNewGuestFieldChange('guest_group', e.target.value)}
              >
                {renderGroupOptions()}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="new-side">צד</label>
              <select
                id="new-side"
                value={newGuestData.side}
                onChange={(e) => handleNewGuestFieldChange('side', e.target.value)}
              >
                <option value="">בחר צד</option>
                <option value="חתן">חתן</option>
                <option value="כלה">כלה</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="new-is-coming">מגיע/ה ?</label>
              <select
                id="new-is-coming"
                value={newGuestData.is_coming}
                onChange={(e) => handleNewGuestFieldChange('is_coming', e.target.value)}
              >
                <option value="don't know">לא יודע/ת</option>
                <option value="yes">כן</option>
                <option value="no">לא</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="new-number-of-guests">מספר אורחים</label>
              <input
                id="new-number-of-guests"
                type="number"
                min="0"
                max="20"
                value={newGuestData.number_of_guests}
                onChange={(e) => handleNewGuestFieldChange('number_of_guests', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="form-actions">
            <button 
              className="save-guest-btn"
              onClick={saveNewGuest}
              disabled={isSavingNewGuest || !newGuestData.firstname.trim() || !newGuestData.lastname.trim()}
            >
              {isSavingNewGuest ? <FaSpinner className="spinner" /> : 'שמור'}
            </button>
            <button 
              className="cancel-btn"
              onClick={hideAddGuestForm}
              disabled={isSavingNewGuest}
            >
              בטל
            </button>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="filter-section">
        <div className="filter-controls">
          <div className="filter-group">
            <label htmlFor="search-guests">חיפוש אורחים:</label>
            <input
              id="search-guests"
              type="text"
              placeholder="חיפוש אורחים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="filter-select"
            />
          </div>
          
          <div className="filter-group">
            <label htmlFor="filter-group">סנן לפי קבוצה:</label>
            <select
              id="filter-group"
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="filter-select"
            >
              <option value="">כל הקבוצות</option>
              {availableGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="filter-side">סנן לפי צד:</label>
            <select
              id="filter-side"
              value={filterSide}
              onChange={(e) => setFilterSide(e.target.value)}
              className="filter-select"
            >
              <option value="">כל הצדדים</option>
              <option value="חתן">חתן</option>
              <option value="כלה">כלה</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="filter-coming">סנן לפי סטטוס הגעה:</label>
            <select
              id="filter-coming"
              value={filterComing}
              onChange={(e) => setFilterComing(e.target.value)}
              className="filter-select"
            >
              <option value="">כל הסטטוסים</option>
              <option value="yes">מגיע</option>
              <option value="no">לא מגיע</option>
              <option value="don't know">לא יודע</option>
            </select>
          </div>

          {user.isAdmin && (
            <div className="filter-group">
              <label htmlFor="filter-delivery">סנן לפי קבלת הודעה:</label>
              <select
                id="filter-delivery"
                value={filterDelivery}
                onChange={(e) => setFilterDelivery(e.target.value)}
                className="filter-select"
              >
                <option value="">הכל</option>
                <option value="sent">נשלחה בהצלחה</option>
                <option value="failed">שליחה נכשלה</option>
                <option value="grey">לא נשלחה</option>
              </select>
            </div>
          )}

          <button
            className="clear-filters-btn"
            onClick={() => {
              setFilterGroup('')
              setFilterSide('')
              setFilterComing('')
              setFilterDelivery('')
              setSearchTerm('')
            }}
            title="נקה את כל הפילטרים"
          >
            נקה פילטרים
          </button>
        </div>
      </div>

      {/* Admin batch-select for customized WA */}
      {user.isAdmin && (
        <div className="batch-select-controls">

          {/* Customized invitation */}
          <div className="batch-select-section">
            <div className="batch-select-section-header">
              <span className="batch-select-label">📲 הזמנה מותאמת</span>
              <span className="eligible-count">
                {editableGuests.filter(g => !g.isDeleted && g.phone_number && !customizedWaSentPhones.has(normalizePhone(g.phone_number))).length} זכאים
              </span>
            </div>
            <div className="batch-select-row">
              <span className="batch-group-label">ראשונים:</span>
              <button className="batch-select-btn batch-btn-small" onClick={handleSelectFirst10Eligible} disabled={isSendingCustomized}>10</button>
              <button className="batch-select-btn batch-btn-large" onClick={handleSelectFirst1000Eligible} disabled={isSendingCustomized} title="פעולה גדולה — בחירת 1000 אורחים">1000 ⚠️</button>
              <span className="batch-group-divider" />
              <span className="batch-group-label">הבאים:</span>
              <button className="batch-select-btn batch-btn-small" onClick={handleSelectNext10Eligible} disabled={isSendingCustomized}>10</button>
              <button className="batch-select-btn batch-btn-large" onClick={handleSelectNext1000Eligible} disabled={isSendingCustomized} title="פעולה גדולה — בחירת 1000 אורחים">1000 ⚠️</button>
            </div>
          </div>

          {/* Second reminder */}
          <div className="batch-select-section">
            <div className="batch-select-section-header">
              <span className="batch-select-label">📩 תזכורת שנייה</span>
              <span className="eligible-count">
                {editableGuests.filter(g => !g.isDeleted && g.phone_number && !secondWaSentPhones.has(normalizePhone(g.phone_number))).length} זכאים
              </span>
            </div>
            <div className="batch-select-row">
              <span className="batch-group-label">ראשונים:</span>
              <button className="batch-select-btn batch-btn-small" onClick={handleSelectFirst10EligibleSecond} disabled={isSendingSecondWa}>10</button>
              <button className="batch-select-btn batch-btn-large" onClick={handleSelectFirst1000EligibleSecond} disabled={isSendingSecondWa} title="פעולה גדולה — בחירת 1000 אורחים">1000 ⚠️</button>
              <span className="batch-group-divider" />
              <span className="batch-group-label">הבאים:</span>
              <button className="batch-select-btn batch-btn-small" onClick={handleSelectNext10EligibleSecond} disabled={isSendingSecondWa}>10</button>
              <button className="batch-select-btn batch-btn-large" onClick={handleSelectNext1000EligibleSecond} disabled={isSendingSecondWa} title="פעולה גדולה — בחירת 1000 אורחים">1000 ⚠️</button>
            </div>
          </div>

        </div>
      )}

      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <span>נבחרו {selectedRows.size} אורחים</span>
          </div>
          <div className="bulk-actions-buttons">
            <button
              className="bulk-delete-btn"
              onClick={handleBulkDelete}
              disabled={isSaving}
            >
              {isSaving ? <FaSpinner className="spinner" /> : '🗑️'} מחק נבחרים
            </button>
          {user.isAdmin && (
            <>
              <button
                className="bulk-message-btn"
                onClick={() => { setMessageChannel('sms'); setShowMessageModal(true) }}
                disabled={isSaving}
              >
                📱 שלח SMS
              </button>
              <button
                className="bulk-whatsapp-btn"
                onClick={() => { setMessageChannel('whatsapp'); setShowMessageModal(true) }}
                disabled={isSaving}
              >
                <span style={{ color: '#25D366' }}>●</span> שלח וואטסאפ
              </button>
              <button
                className="bulk-customized-wa-btn"
                onClick={() => setShowCustomizedConfirm(true)}
                disabled={isSendingCustomized || isSaving}
              >
                {isSendingCustomized ? <FaSpinner className="spinner" /> : '📲'} שלח WA מותאם
              </button>
              <button
                className="bulk-second-wa-btn"
                onClick={() => setShowSecondWaConfirm(true)}
                disabled={isSendingSecondWa || isSaving}
              >
                {isSendingSecondWa ? <FaSpinner className="spinner" /> : '📩'} שלח WA שני
              </button>
            </>
          )}
            <button
              className="bulk-clear-btn"
              onClick={() => setSelectedRows(new Set())}
            >
              ביטול בחירה
            </button>
          </div>
        </div>
      )}

      <div className="table-wrapper" ref={tableRef}>
        <table className="guest-table editable">
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '10%' }} />
            {user.isAdmin && <col style={{ width: '8%' }} />}
            <col style={{ width: '4%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  disabled={visibleGuests.length === 0}
                />
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('firstname')}
              >
                שם פרטי {getSortIcon('firstname')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('lastname')}
              >
                שם משפחה {getSortIcon('lastname')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('phone_number')}
              >
                טלפון {getSortIcon('phone_number')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('guest_group')}
              >
                קבוצה {getSortIcon('guest_group')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('side')}
              >
                צד {getSortIcon('side')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('is_coming')}
              >
                מגיע/ה ? {getSortIcon('is_coming')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('number_of_guests')}
              >
                מספר אורחים {getSortIcon('number_of_guests')}
              </th>
              {user.isAdmin && (
                <th 
                  className="sortable"
                  onClick={() => handleSort('statusid')}
                >
                  קבלת הודעה {getSortIcon('statusid')}
                </th>
              )}
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {visibleGuests.map((guest) => (
              <React.Fragment key={guest.id}>
              <tr
                className={`${guest.isNew ? 'new-row' : ''} ${focusedGuestId === guest.id ? 'focused' : ''} ${selectedRows.has(guest.id) ? 'selected' : ''}`}
                data-guest-id={guest.id}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.has(guest.id)}
                    onChange={(e) => handleSelectRow(guest.id, e.target.checked)}
                  />
                </td>
                <td 
                  className={editingCell?.guestId === guest.id && editingCell?.field === 'firstname' ? 'cell-editing' : 'cell-clickable'}
                  onClick={() => handleCellClick(guest.id, 'firstname')}
                >
                  {editingCell?.guestId === guest.id && editingCell?.field === 'firstname' ? (
                    <div className="input-container">
                      <input
                        type="text"
                        value={guest.firstname}
                        onChange={(e) => {
                          updateGuest(guest.id, 'firstname', e.target.value)
                          triggerDebouncedSave(guest.id)
                        }}
                        placeholder="שם פרטי"
                        disabled={isSaving}
                        className={validationErrors[guest.id]?.firstname ? 'error' : ''}
                        autoFocus
                      />
                      {validationErrors[guest.id]?.firstname && (
                        <div className="error-message">
                          <FaExclamationTriangle />
                          {validationErrors[guest.id].firstname}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="cell-content-wrapper">
                      <span className="cell-content">{guest.firstname || 'לחץ לעריכה'}</span>
                      {savingCell?.guestId === guest.id && savingCell?.field === 'firstname' && (
                        <FaSpinner className="saving-indicator" />
                      )}
                    </div>
                  )}
                </td>
                <td 
                  className={editingCell?.guestId === guest.id && editingCell?.field === 'lastname' ? 'cell-editing' : 'cell-clickable'}
                  onClick={() => handleCellClick(guest.id, 'lastname')}
                >
                  {editingCell?.guestId === guest.id && editingCell?.field === 'lastname' ? (
                    <div className="input-container">
                      <input
                        type="text"
                        value={guest.lastname}
                        onChange={(e) => {
                          updateGuest(guest.id, 'lastname', e.target.value)
                          triggerDebouncedSave(guest.id)
                        }}
                        placeholder="שם משפחה"
                        disabled={isSaving}
                        className={validationErrors[guest.id]?.lastname ? 'error' : ''}
                        autoFocus
                      />
                      {validationErrors[guest.id]?.lastname && (
                        <div className="error-message">
                          <FaExclamationTriangle />
                          {validationErrors[guest.id].lastname}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="cell-content">{guest.lastname || 'לחץ לעריכה'}</span>
                  )}
                </td>
                <td 
                  className={editingCell?.guestId === guest.id && editingCell?.field === 'phone_number' ? 'cell-editing' : 'cell-clickable'}
                  onClick={() => handleCellClick(guest.id, 'phone_number')}
                >
                  {editingCell?.guestId === guest.id && editingCell?.field === 'phone_number' ? (
                    <div className="input-container">
                      <input
                        type="tel"
                        value={guest.phone_number}
                        onChange={(e) => {
                          updateGuest(guest.id, 'phone_number', e.target.value)
                          triggerDebouncedSave(guest.id)
                        }}
                        placeholder="מספר טלפון"
                        disabled={isSaving}
                        className={validationErrors[guest.id]?.phone_number ? 'error' : ''}
                        autoFocus
                      />
                      {validationErrors[guest.id]?.phone_number && (
                        <div className="error-message">
                          <FaExclamationTriangle />
                          {validationErrors[guest.id].phone_number}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="cell-content">{guest.phone_number || 'לחץ לעריכה'}</span>
                  )}
                </td>
                <td 
                  className={editingCell?.guestId === guest.id && editingCell?.field === 'guest_group' ? 'cell-editing' : 'cell-clickable'}
                  onClick={() => handleCellClick(guest.id, 'guest_group')}
                >
                  {editingCell?.guestId === guest.id && editingCell?.field === 'guest_group' ? (
                    <div className="input-container">
                      <select
                        value={guest.guest_group || ''}
                        onChange={(e) =>
                          handleDropdownChange(guest.id, 'guest_group', e.target.value)
                        }
                        disabled={isSaving}
                        autoFocus
                      >
                        {renderGroupOptions()}
                      </select>
                    </div>
                  ) : (
                    guest.guest_group ? (
                      <span 
                        className="group-badge-cell"
                        style={{ backgroundColor: getGroupColor(guest.guest_group) }}
                      >
                        {guest.guest_group}
                      </span>
                    ) : (
                      <span className="cell-content empty-group">לחץ לעריכה</span>
                    )
                  )}
                </td>
                <td 
                  className={editingCell?.guestId === guest.id && editingCell?.field === 'side' ? 'cell-editing' : 'cell-clickable'}
                  onClick={() => handleCellClick(guest.id, 'side')}
                >
                  {editingCell?.guestId === guest.id && editingCell?.field === 'side' ? (
                    <div className="input-container">
                      <select
                        value={guest.side || ''}
                        onChange={(e) =>
                          handleDropdownChange(guest.id, 'side', e.target.value)
                        }
                        disabled={isSaving}
                        autoFocus
                      >
                        <option value="">בחר צד</option>
                        <option value="חתן">חתן</option>
                        <option value="כלה">כלה</option>
                      </select>
                    </div>
                  ) : (
                    <span className="cell-content">{guest.side || 'לחץ לעריכה'}</span>
                  )}
                </td>
                <td 
                  className={`rsvp-cell ${editingCell?.guestId === guest.id && editingCell?.field === 'is_coming' ? 'cell-editing' : 'cell-clickable'}`}
                  onClick={() => handleCellClick(guest.id, 'is_coming')}
                >
                  {editingCell?.guestId === guest.id && editingCell?.field === 'is_coming' ? (
                    <select
                      value={guest.is_coming}
                      onChange={(e) =>
                        handleDropdownChange(guest.id, 'is_coming', e.target.value)
                      }
                      disabled={isSaving}
                      className={`rsvp-select ${guest.is_coming}`}
                      autoFocus
                    >
                      {getRsvpOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`rsvp-badge ${guest.is_coming}`}>
                      {getRsvpOptions().find(
                        (opt) => opt.value === guest.is_coming
                      )?.label || guest.is_coming}
                    </span>
                  )}
                </td>
                <td 
                  className={editingCell?.guestId === guest.id && editingCell?.field === 'number_of_guests' ? 'cell-editing' : 'cell-clickable'}
                  onClick={() => handleCellClick(guest.id, 'number_of_guests')}
                >
                  {editingCell?.guestId === guest.id && editingCell?.field === 'number_of_guests' ? (
                    <div className="input-container">
                      <input
                        type="number"
                        min="0"
                        max="20"
                        value={guest.number_of_guests}
                        onChange={(e) => {
                          updateGuest(guest.id, 'number_of_guests', e.target.value)
                          triggerDebouncedSave(guest.id)
                        }}
                        disabled={isSaving}
                        className={validationErrors[guest.id]?.number_of_guests ? 'error' : ''}
                        autoFocus
                      />
                      {validationErrors[guest.id]?.number_of_guests && (
                        <div className="error-message">
                          <FaExclamationTriangle />
                          {validationErrors[guest.id].number_of_guests}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="cell-content">{guest.number_of_guests}</span>
                  )}
                </td>
                {user.isAdmin && (
                  <td className="status-cell">
                    {getStatusIndicator(guest)}
                  </td>
                )}
                <td>
                  {editingCell?.guestId !== guest.id && (
                    <button
                      className="delete-btn"
                      onClick={() => requestDeleteGuest(guest.id)}
                      disabled={isSaving || deletingGuestId === guest.id}
                      title={`מחק את ${guest.firstname} ${guest.lastname}`}
                    >
                      {deletingGuestId === guest.id ? (
                        <FaSpinner className="spinner" />
                      ) : (
                        '🗑️'
                      )}
                    </button>
                  )}
                </td>
              </tr>
              {validationErrors[guest.id]?.general && (
                <tr className="row-save-error">
                  <td colSpan={user.isAdmin ? 10 : 9}>
                    <span className="save-error-msg">
                      <FaExclamationTriangle /> {validationErrors[guest.id].general}
                    </span>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}

            {visibleGuests.length === 0 && (
              <tr>
                <td colSpan={user.isAdmin ? "10" : "9"} className="no-guests">
                  {searchTerm ? 'לא נמצאו אורחים התואמים לחיפוש.' : 'אין אורחים. לחץ על "הוסף אורח חדש" כדי להתחיל.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Message Modal */}
      {showMessageModal && createPortal(
        <div className="modal-overlay">
          <div className="message-modal">
            <div className="modal-header">
              <h3>
                {messageChannel === 'whatsapp' ? '🟢 שלח וואטסאפ' : '📱 שלח SMS'} ל-{selectedRows.size} אורחים
              </h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowMessageModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="selected-guests-info">
                <p>אורחים נבחרים עם מספר טלפון:</p>
                <ul>
                  {editableGuests
                    .filter(guest => selectedRows.has(guest.id) && guest.phone_number)
                    .map(guest => (
                      <li key={guest.id}>
                        {guest.firstname} {guest.lastname} - {guest.phone_number}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              {messageChannel === 'whatsapp' ? (
                <button
                  className="send-whatsapp-btn"
                  onClick={handleSendWhatsAppFirstMessage}
                  disabled={isSendingMessage}
                >
                  {isSendingMessage ? <FaSpinner className="spinner" /> : '🟢'} שלח וואטסאפ
                </button>
              ) : (
                <button
                  className="send-message-btn"
                  onClick={handleSendMessage}
                  disabled={isSendingMessage}
                >
                  {isSendingMessage ? <FaSpinner className="spinner" /> : '📱'} שלח SMS
                </button>
              )}
              <button
                className="cancel-message-btn"
                onClick={() => setShowMessageModal(false)}
                disabled={isSendingMessage}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}


      {/* Second WA Confirmation Modal */}
      {showSecondWaConfirm && createPortal(
        <div className="modal-overlay">
          <div className="message-modal">
            <div className="modal-header">
              <h3>📩 שלח תזכורת שנייה ב-WhatsApp</h3>
              <button className="close-modal-btn" onClick={() => setShowSecondWaConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              {(() => {
                const selectedGuests = editableGuests.filter(g => selectedRows.has(g.id))
                const alreadySentGuests = selectedGuests.filter(g => g.phone_number && secondWaSentPhones.has(normalizePhone(g.phone_number)))
                const willSend = selectedGuests.filter(g => g.phone_number && !secondWaSentPhones.has(normalizePhone(g.phone_number))).length
                const noPhone = selectedGuests.filter(g => !g.phone_number).length
                const totalWillSend = willSend + (includeAlreadySentSecond ? alreadySentGuests.length : 0)
                return (
                  <div style={{ lineHeight: 2 }}>
                    <p>נבחרו <strong>{selectedRows.size}</strong> אורחים</p>
                    <label className="modal-resend-label">
                      <input
                        type="checkbox"
                        checked={includeAlreadySentSecond}
                        onChange={e => setIncludeAlreadySentSecond(e.target.checked)}
                      />
                      <span className="modal-resend-checkbox" />
                      שלח שוב למי שכבר קיבל ({alreadySentGuests.length})
                    </label>
                    {!includeAlreadySentSecond && (
                      <p>כבר קיבלו תזכורת שנייה (ידולגו): <strong>{alreadySentGuests.length}</strong></p>
                    )}
                    <p>ללא מספר טלפון (ידולגו): <strong>{noPhone}</strong></p>
                    <p>יקבלו הודעה: <strong>{totalWillSend}</strong></p>
                    <p style={{ color: '#888', fontSize: '0.85em' }}>תבנית: rsvp_invitation_second_ut</p>
                  </div>
                )
              })()}
            </div>
            <div className="modal-footer">
              <button
                className="send-whatsapp-btn"
                onClick={handleSendSecondWa}
                disabled={isSendingSecondWa}
              >
                {isSendingSecondWa ? <FaSpinner className="spinner" /> : '📩'} שלח
              </button>
              <button
                className="cancel-message-btn"
                onClick={() => setShowSecondWaConfirm(false)}
                disabled={isSendingSecondWa}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Customized WA Confirmation Modal */}
      {showCustomizedConfirm && createPortal(
        <div className="modal-overlay">
          <div className="message-modal">
            <div className="modal-header">
              <h3>📲 שלח הזמנה מותאמת ב-WhatsApp</h3>
              <button className="close-modal-btn" onClick={() => setShowCustomizedConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              {(() => {
                const selectedGuests = editableGuests.filter(g => selectedRows.has(g.id))
                const alreadySentGuests = selectedGuests.filter(g => g.phone_number && customizedWaSentPhones.has(normalizePhone(g.phone_number)))
                const willSend = selectedGuests.filter(g => g.phone_number && !customizedWaSentPhones.has(normalizePhone(g.phone_number))).length
                const noPhone = selectedGuests.filter(g => !g.phone_number).length
                const totalWillSend = willSend + (includeAlreadySentCustomized ? alreadySentGuests.length : 0)
                return (
                  <div style={{ lineHeight: 2 }}>
                    <p>נבחרו <strong>{selectedRows.size}</strong> אורחים</p>
                    <label className="modal-resend-label">
                      <input
                        type="checkbox"
                        checked={includeAlreadySentCustomized}
                        onChange={e => setIncludeAlreadySentCustomized(e.target.checked)}
                      />
                      <span className="modal-resend-checkbox" />
                      שלח שוב למי שכבר קיבל ({alreadySentGuests.length})
                    </label>
                    {!includeAlreadySentCustomized && (
                      <p>כבר קיבלו הזמנה מותאמת (ידולגו): <strong>{alreadySentGuests.length}</strong></p>
                    )}
                    <p>ללא מספר טלפון (ידולגו): <strong>{noPhone}</strong></p>
                    <p>יקבלו הודעה: <strong>{totalWillSend}</strong></p>
                    <p style={{ color: '#888', fontSize: '0.85em' }}>תבנית: customized_model</p>
                  </div>
                )
              })()}
            </div>
            <div className="modal-footer">
              <button
                className="send-whatsapp-btn"
                onClick={handleSendCustomizedWhatsApp}
                disabled={isSendingCustomized}
              >
                {isSendingCustomized ? <FaSpinner className="spinner" /> : '📲'} שלח
              </button>
              <button
                className="cancel-message-btn"
                onClick={() => setShowCustomizedConfirm(false)}
                disabled={isSendingCustomized}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Single Delete Confirmation Modal */}
      {deleteConfirm && createPortal(
        <div className="modal-overlay">
          <div className="message-modal">
            <div className="modal-header">
              <h3>🗑️ מחק אורח</h3>
              <button className="close-modal-btn" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>האם אתה בטוח שברצונך למחוק את <strong>{deleteConfirm.name}</strong>?</p>
            </div>
            <div className="modal-footer">
              <button
                className="bulk-delete-btn"
                onClick={confirmDeleteGuest}
                disabled={deletingGuestId === deleteConfirm.id}
              >
                {deletingGuestId === deleteConfirm.id ? <FaSpinner className="spinner" /> : '🗑️'} מחק
              </button>
              <button
                className="cancel-message-btn"
                onClick={() => setDeleteConfirm(null)}
                disabled={deletingGuestId === deleteConfirm.id}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteConfirm && createPortal(
        <div className="modal-overlay">
          <div className="message-modal">
            <div className="modal-header">
              <h3>🗑️ מחק אורחים נבחרים</h3>
              <button className="close-modal-btn" onClick={() => setBulkDeleteConfirm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>האם אתה בטוח שברצונך למחוק <strong>{selectedRows.size}</strong> אורחים?</p>
              <p style={{ color: '#888', fontSize: '0.85em' }}>פעולה זו אינה ניתנת לביטול</p>
            </div>
            <div className="modal-footer">
              <button
                className="bulk-delete-btn"
                onClick={confirmBulkDelete}
                disabled={isSaving}
              >
                {isSaving ? <FaSpinner className="spinner" /> : '🗑️'} מחק
              </button>
              <button
                className="cancel-message-btn"
                onClick={() => setBulkDeleteConfirm(false)}
                disabled={isSaving}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  )
}

export default EditableGuestTable
